import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatError } from "@/lib/api";
import { Briefcase, GraduationCap, Wrench, Send, Link as LinkIcon } from "lucide-react";

const initial = { name: "", email: "", phone: "", role: "teacher", grade: "", subject: "", resume: "", message: "" };

export default function Careers() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.resume && !/^https?:\/\//i.test(form.resume.trim())) {
      toast.error(t.careers.err_link);
      return;
    }
    setSending(true);
    try {
      const isTeacher = form.role === "teacher";
      const roleLabel = isTeacher ? t.careers.role_teacher : t.careers.role_staff;
      const programParts = [roleLabel];
      if (isTeacher && form.grade) programParts.push(form.grade);
      if (isTeacher && form.subject) programParts.push(form.subject);
      const messageParts = [];
      if (form.resume) messageParts.push(`CV: ${form.resume}`);
      if (form.message) messageParts.push(form.message);

      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        type: "career",
        program: programParts.join(" · "),
        message: messageParts.join("\n\n"),
      });
      toast.success(t.careers.success);
      setForm(initial);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const field = "w-full h-12 bg-transparent border-b border-icdh-navy/25 focus:border-icdh-red outline-none text-icdh-navy placeholder:text-icdh-navy/40 transition-colors";
  const isTeacher = form.role === "teacher";

  return (
    <section id="careers" data-testid="careers-section" className="bg-icdh-white py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <Reveal><Overline>{t.careers.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-icdh-navy">{t.careers.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-icdh-navy/75 text-lg max-w-lg">{t.careers.lead}</p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="mt-12 flex items-center gap-4 text-icdh-navy/70">
              <Briefcase className="text-icdh-red" />
              <span className="text-sm">
                <a href="mailto:icdhhaiti@gmail.com" className="hover:text-icdh-red transition-colors">icdhhaiti@gmail.com</a>
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="careers-form" className="bg-icdh-surface p-8 md:p-12 border border-icdh-navy/10 space-y-7">
            {/* Role selector */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-icdh-red mb-4">{t.careers.role_label}</p>
              <div className="grid grid-cols-2 gap-3" data-testid="role-picker">
                <button type="button" onClick={() => setForm({ ...form, role: "teacher" })} data-testid="role-teacher"
                  className={`h-14 px-4 flex items-center gap-3 border text-sm font-medium transition-colors ${form.role === "teacher" ? "bg-icdh-navy text-icdh-white border-icdh-navy" : "border-icdh-navy/25 text-icdh-navy hover:border-icdh-red"}`}>
                  <GraduationCap size={16} />{t.careers.role_teacher}
                </button>
                <button type="button" onClick={() => setForm({ ...form, role: "staff" })} data-testid="role-staff"
                  className={`h-14 px-4 flex items-center gap-3 border text-sm font-medium transition-colors ${form.role === "staff" ? "bg-icdh-navy text-icdh-white border-icdh-navy" : "border-icdh-navy/25 text-icdh-navy hover:border-icdh-red"}`}>
                  <Wrench size={16} />{t.careers.role_staff}
                </button>
              </div>
            </div>

            <input required data-testid="careers-name" className={field} placeholder={t.admissions.form.name} value={form.name} onChange={set("name")} />
            <input required type="email" data-testid="careers-email" className={field} placeholder={t.admissions.form.email} value={form.email} onChange={set("email")} />
            <input data-testid="careers-phone" className={field} placeholder={t.admissions.form.phone} value={form.phone} onChange={set("phone")} />

            {isTeacher && (
              <>
                <input data-testid="careers-grade" className={field} placeholder={t.careers.grade_placeholder} value={form.grade} onChange={set("grade")} />
                <input data-testid="careers-subject" className={field} placeholder={t.careers.subject_placeholder} value={form.subject} onChange={set("subject")} />
              </>
            )}

            <div>
              <div className="flex items-center gap-2 text-icdh-navy/70 text-xs mb-2">
                <LinkIcon size={13} />
                <span className="uppercase tracking-[0.2em]">{t.careers.resume_label}</span>
              </div>
              <input type="url" data-testid="careers-resume" className={field} placeholder={t.careers.resume_placeholder} value={form.resume} onChange={set("resume")} />
              <p className="mt-2 text-xs text-icdh-navy/50">{t.careers.resume_hint}</p>
            </div>

            <textarea rows={4} data-testid="careers-message" className={`${field} h-auto resize-none pt-3`} placeholder={t.careers.message_placeholder} value={form.message} onChange={set("message")} />

            <button type="submit" disabled={sending} data-testid="careers-submit"
              className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
              <Send size={14} />{sending ? "..." : t.careers.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
